import type { ScheduleBoundary } from "@/player/lib/scheduling/types";

export type BoundaryTransition = "start" | "end";

export interface BoundarySchedulerCallbacks {
  onTransition: (boundary: ScheduleBoundary, transition: BoundaryTransition) => void;
  /**
   * Clock used for delays and the fire-time staleness re-check. Callers pass
   * the server-corrected clock — boundary instants are server-stamped.
   */
  nowMs?: () => number;
  /**
   * Called once per arm() generation when at least one transition fired far
   * past its instant (suspend/resume burst) and was suppressed — the caller
   * should re-derive current state (force a resync) instead of replaying a
   * pile of historical transitions, each of which would advance() the bus.
   */
  onStaleBurst?: () => void;
}

/** How late a transition may fire and still be applied as a live switch. */
export const STALE_TRANSITION_TOLERANCE_MS = 90_000;

/**
 * Arms local timers from boundaries[] so continuous programs switch at
 * daypart start/end without waiting for the next sync poll.
 */
export class BoundaryScheduler {
  private timers: number[] = [];
  private staleNotified = false;

  constructor(private readonly callbacks: BoundarySchedulerCallbacks) {}

  private nowMs(): number {
    return this.callbacks.nowMs?.() ?? Date.now();
  }

  arm(boundaries: ScheduleBoundary[], now = this.nowMs()): void {
    this.disarm();
    this.staleNotified = false;

    const fireOrSuppress = (
      boundary: ScheduleBoundary,
      transition: BoundaryTransition,
      scheduledMs: number,
    ) => {
      const lateBy = this.nowMs() - scheduledMs;
      if (lateBy > STALE_TRANSITION_TOLERANCE_MS) {
        // A wake-after-suspend delivers every armed timer in one burst;
        // replaying hours of transitions would advance() once per boundary.
        // Suppress them and tell the caller ONCE to re-derive from fresh data.
        if (!this.staleNotified) {
          this.staleNotified = true;
          this.callbacks.onStaleBurst?.();
        }
        return;
      }
      this.callbacks.onTransition(boundary, transition);
    };

    for (const boundary of boundaries) {
      const startMs = new Date(boundary.startsAt).getTime();
      const endMs = new Date(boundary.endsAt).getTime();

      if (startMs > now) {
        const timer = window.setTimeout(
          () => fireOrSuppress(boundary, "start", startMs),
          startMs - now,
        );
        this.timers.push(timer);
      }

      if (endMs > now) {
        const timer = window.setTimeout(
          () => fireOrSuppress(boundary, "end", endMs),
          endMs - now,
        );
        this.timers.push(timer);
      }
    }
  }

  disarm(): void {
    for (const id of this.timers) window.clearTimeout(id);
    this.timers = [];
  }
}
